import { isAccommodationV2Item, isBedbankItem, isExperienceItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'

export type ExperiencesPassportAccommodationItem =
  | App.Checkout.InstantBookingLEHotelItem
  | App.Checkout.BedbankHotelItem
  | App.Checkout.AccommodationV2Item

export function getExperiencesPassportAccommodationItems(items: Array<App.Checkout.AnyItem>): Array<ExperiencesPassportAccommodationItem> {
  return items.filter((item): item is ExperiencesPassportAccommodationItem =>
    isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isAccommodationV2Item(item))
}

export function getExperiencesPassportCheckoutKey(cart: App.CheckoutCartState): string | undefined {
  const accommodations = getExperiencesPassportAccommodationItems(cart.items)
  if (!accommodations.length && !cart.items.some(isExperiencesPassportItem)) return undefined

  return JSON.stringify([
    cart.transactionKey,
    cart.regionCode,
    cart.currencyCode,
    ...accommodations
      .map(item => [item.itemId, item.offerId, item.checkIn, item.checkOut])
      .sort((first, second) => first[0].localeCompare(second[0])),
  ])
}

export function isExperiencesPassportItem(item: App.Checkout.AnyItem): item is App.Checkout.ExperienceItem {
  return isExperienceItem(item) && !!item.experiencesPassport
}

function getExperiencesPassportItemKey(item: App.Checkout.ExperienceItem): string {
  // The parent item ID identifies the booked room; roomId only identifies its room type.
  return JSON.stringify([item.accommodationParentItemId, item.experienceId])
}

export function reconcileExperiencesPassportCheckoutItems(
  currentItems: Array<App.Checkout.AnyItem>,
  desiredItems: Array<App.Checkout.ExperienceItem>,
): Array<App.Checkout.AnyItem> {
  const desiredByKey = new Map(desiredItems.map(item => [getExperiencesPassportItemKey(item), item]))
  const keptKeys = new Set<string>()
  const nextItems: Array<App.Checkout.AnyItem> = []
  let changed = false

  for (const item of currentItems) {
    if (!isExperiencesPassportItem(item)) {
      nextItems.push(item)
      continue
    }

    const key = getExperiencesPassportItemKey(item)
    const desired = desiredByKey.get(key)
    if (!desired || keptKeys.has(key)) {
      changed = true
      continue
    }

    keptKeys.add(key)
    const updated = { ...desired, itemId: item.itemId, transactionKey: item.transactionKey }
    if (JSON.stringify(item) === JSON.stringify(updated)) {
      nextItems.push(item)
    } else {
      nextItems.push(updated)
      changed = true
    }
  }

  for (const [key, desired] of desiredByKey) {
    if (keptKeys.has(key)) continue
    nextItems.push(desired)
    changed = true
  }

  return changed ? nextItems : currentItems
}

export function getExperiencesPassportCheckoutChanges(
  currentItems: Array<App.Checkout.AnyItem>,
  desiredItems: Array<App.Checkout.ExperienceItem>,
): {
  toAdd: Array<App.Checkout.ExperienceItem>
  toUpdate: Array<App.Checkout.ExperienceItem>
  toRemove: Array<string>
} {
  const nextItems = reconcileExperiencesPassportCheckoutItems(currentItems, desiredItems)
  const currentById = new Map(currentItems.filter(isExperiencesPassportItem).map(item => [item.itemId, item]))
  const nextById = new Map(nextItems.filter(isExperiencesPassportItem).map(item => [item.itemId, item]))

  return {
    toAdd: [...nextById.values()].filter(item => !currentById.has(item.itemId)),
    toUpdate: [...nextById.values()].filter(item => currentById.has(item.itemId) && currentById.get(item.itemId) !== item),
    toRemove: [...currentById.keys()].filter(itemId => !nextById.has(itemId)),
  }
}
